import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Calendar, MessageSquare, ShoppingCart, Users, Check, CheckCheck, MessageCircle } from 'lucide-react';
import { motion, useInView } from 'framer-motion';
import { useIsMobile } from '@/hooks/use-mobile';
import IntegrationsFlowMap from '@/components/IntegrationsFlowMap';
import {
  RealTimeOrdersAnimation,
  CRMAutoUpdatedAnimation,
  AutomatedFollowUpsAnimation,
  PersonalizedRepliesAnimation,
  AlwaysOnAnimation
} from './components/NewFeatureAnimations';

const Integrations = () => {
  const containerRef = useRef(null);
  const featuresRef = useRef(null);
  const { isMobile } = useIsMobile();
  const featuresInView = useInView(featuresRef, { amount: 0.1, once: true });

  // Agent modes configuration
  const agentModes = [
    {
      id: 'customerService',
      label: 'Customer Service',
      icon: <MessageSquare className="h-4 w-4" />
    },
    {
      id: 'orderProcessing',
      label: 'Order Processing',
      icon: <ShoppingCart className="h-4 w-4" />
    },
    {
      id: 'leadQualification',
      label: 'Lead Qualification',
      icon: <Users className="h-4 w-4" />
    },
    {
      id: 'conversationalSupport',
      label: 'Support',
      icon: <MessageCircle className="h-4 w-4" />
    },
    {
      id: 'scheduling',
      label: 'Scheduling',
      icon: <Calendar className="h-4 w-4" />
    }
  ];

  const [currentAgentIndex, setCurrentAgentIndex] = useState(0);
  const [timerProgress, setTimerProgress] = useState(0);

  // Auto-advancing timer effect
  useEffect(() => {
    const timerDuration = 5000; // 5 seconds
    const updateInterval = 50; // Update every 50ms for smooth animation
    const steps = timerDuration / updateInterval;
    let currentStep = 0;

    const timer = setInterval(() => {
      currentStep++;
      const progress = (currentStep / steps) * 100;
      setTimerProgress(progress);

      if (currentStep >= steps) {
        // Advance to next agent
        setCurrentAgentIndex((prev) => (prev + 1) % agentModes.length);
        setTimerProgress(0);
        currentStep = 0;
      }
    }, updateInterval);

    return () => {
      clearInterval(timer);
    };
  }, [currentAgentIndex]);

  // Handle manual tab change
  const handleAgentChange = (index: number) => {
    setCurrentAgentIndex(index);
    setTimerProgress(0);
  };


  return (
    <div ref={containerRef} className="landing-gradient-bg">

      {/* Animated Grid Background */}

      {/* Hero Section */}
      <motion.div
        className={`relative z-10 landing-hero ${isMobile ? 'pt-16 pb-6 px-4' : 'pt-24 pb-12 px-8 md:px-16 xl:px-32 2xl:px-40'} overflow-hidden`}
      >
        <div className="max-w-screen-2xl mx-auto">
          <div className={`grid items-center ${isMobile ? 'grid-cols-1 gap-8' : 'lg:grid-cols-2 gap-12'}`}>
            {/* Left side - Content */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className={isMobile ? 'text-center' : 'text-left'}
            >
              <h1 className={`font-bold text-slate-900 dark:text-white leading-tight mb-4 ${isMobile ? 'text-3xl md:text-4xl' : 'text-4xl md:text-5xl'}`}>
                Automate your business with
                <br />
                <span className="text-slate-900 dark:text-white">
                  Synkro AI
                </span>
              </h1>
              <p className={`text-black dark:text-slate-300 leading-relaxed mb-6 ${isMobile ? 'text-base max-w-sm mx-auto' : 'text-lg max-w-3xl'}`}>
                Turn WhatsApp, voice calls, and internal workflows into fully automated customer service channels. Zero setup needed.
              </p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className={isMobile ? 'flex justify-center' : ''}
              >
                <a
                  href="https://wa.me/9613309293"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <Button
                    size={isMobile ? "default" : "lg"}
                    className={`relative bg-black/10 dark:bg-white/10 backdrop-blur-md hover:bg-black/20 dark:hover:bg-white/20 border border-black/30 dark:border-white/30 text-black dark:text-white hover:text-black dark:hover:text-white drop-shadow-lg dark:drop-shadow-none transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-black/20 dark:hover:shadow-white/20 overflow-hidden group ${isMobile ? 'text-base px-6 py-3' : 'text-lg px-8 py-4'}`}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 ease-out" />
                    <span className="relative z-10 font-medium flex items-center">
                      Book a Call
                      <ArrowRight className={`ml-2 ${isMobile ? 'h-4 w-4' : 'h-5 w-5'}`} />
                    </span>
                  </Button>
                </a>
              </motion.div>
            </motion.div>

            {/* Right side - Animated Hero Component */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.8, ease: "easeOut" }}
              className="flex w-full relative z-10"
            >
              <WhatsAppChatFlow isMobile={isMobile} />
            </motion.div>
          </div>
        </div>
      </motion.div>



      {/* Agent Mode Selector */}
      <motion.div
        className={`relative z-10 ${isMobile ? 'px-4 pt-16 pb-8' : 'px-8 md:px-16 xl:px-32 pt-24 pb-12'}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-screen-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className={`font-bold text-slate-900 dark:text-white ${isMobile ? 'text-3xl' : 'text-4xl'}`}>
              See how Synkro automates your workflow
            </h2>
          </div>

          {/* Tab Selector and Flow Map Container */}
          <div className="flex justify-center">
            <div className="w-full max-w-screen-2xl">
              {/* Tab Selector */}
              <div className={`flex ${isMobile ? 'flex-row flex-wrap gap-1' : ''} bg-slate-100/50 dark:bg-slate-800/50 rounded-t-lg p-2`}>
                {agentModes.map((mode, index) => (
                  <button
                    key={mode.id}
                    onClick={() => handleAgentChange(index)}
                    className={`relative px-4 py-2 rounded-md font-medium text-sm transition-all duration-150 ease-in-out flex items-center gap-2 ${currentAgentIndex === index
                      ? 'text-black dark:text-white bg-primary/20'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-accent/70 hover:text-foreground'
                      } ${isMobile ? 'justify-center flex-1 min-w-0' : 'flex-1 justify-center'}`}
                  >
                    {mode.icon}
                    {!isMobile && mode.label}
                    {currentAgentIndex === index && (
                      <motion.div
                        className="absolute bottom-0 left-0 h-0.5 bg-primary rounded-full"
                        style={{ width: `${timerProgress}%` }}
                        transition={{ duration: 0.05 }}
                        layoutId="agentTabTimer"
                      />
                    )}
                  </button>
                ))}
              </div>

              {/* Flow Map */}
              <div className="relative z-10">
                <IntegrationsFlowMap agentType={agentModes[currentAgentIndex].id as any} height="24rem" />
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Features List Section */}
      <section className={`relative z-10 landing-section ${isMobile ? 'py-16 px-4' : 'py-24 px-8 md:px-16 xl:px-32 2xl:px-40'}`}>
        <div className="max-w-screen-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.8 }}
            className={`text-center ${isMobile ? 'mb-12' : 'mb-20'}`}
          >
            <h2 className={`font-bold text-slate-900 dark:text-white mb-6 ${isMobile ? 'text-3xl' : 'text-5xl'}`}>
              Powerful Features
            </h2>
            <p className={`${isMobile ? 'text-base' : 'text-xl'} text-black dark:text-slate-300 max-w-2xl mx-auto`}>
              Everything you need to automate, integrate, and scale your business operations
            </p>
          </motion.div>

          <div ref={featuresRef} className="space-y-8 md:space-y-12">
            {[
              {
                title: "Real-Time Orders & Scheduling",
                description: "Turn chats into confirmed bookings or orders instantly—no delays, no missed customers.",
                Animation: RealTimeOrdersAnimation
              },
              {
                title: "CRM Automatically Updated",
                description: "Customer profiles stay clean and current with automatic syncing across your tools.",
                Animation: CRMAutoUpdatedAnimation
              },
              {
                title: "Automated Follow-Ups & Reminders",
                description: "Send smart reminders and follow-ups automatically to reduce no-shows and boost repeats.",
                Animation: AutomatedFollowUpsAnimation
              },
              {
                title: "Personalized Replies",
                description: "Replies stay on-brand and natural, adapting to each customer and conversation context.",
                Animation: PersonalizedRepliesAnimation
              },
              {
                title: "24/7 Always-On",
                description: "Stay responsive day and night with dependable uptime and always-on automation.",
                Animation: AlwaysOnAnimation
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={featuresInView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1, duration: 0.8 }}
                className={`flex ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} ${isMobile ? 'flex-col' : ''} items-center gap-8 md:gap-16`}
              >
                <div className={`flex-1 ${isMobile ? 'order-1' : ''} space-y-4`}>
                  <div className="flex items-center gap-4">
                    <h3 className={`${isMobile ? 'text-2xl' : 'text-3xl'} font-bold text-slate-900 dark:text-white`}>
                      {feature.title}
                    </h3>
                  </div>
                  <p className={`${isMobile ? 'text-base' : 'text-lg'} text-black dark:text-slate-300 leading-relaxed max-w-2xl`}>
                    {feature.description}
                  </p>
                </div>
                <div className={`flex-1 ${isMobile ? 'order-2 w-full' : ''} relative`}>
                  <div className={`w-full h-64 md:h-80 rounded-3xl bg-gradient-to-br ${index % 4 === 0 ? 'from-purple-400/20 to-blue-400/20' : index % 4 === 1 ? 'from-blue-400/20 to-cyan-400/20' : index % 4 === 2 ? 'from-cyan-400/20 to-purple-400/20' : 'from-pink-400/20 to-purple-400/20'} backdrop-blur-sm border border-white/20 dark:border-white/10 shadow-xl flex items-center justify-center overflow-hidden`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-white/10 rounded-3xl pointer-events-none" />
                    <feature.Animation />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WhatsApp Contact Section */}
      <motion.div
        className={`relative z-10 ${isMobile ? 'px-4 py-16' : 'px-8 md:px-16 xl:px-32 py-24'}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <div className="max-w-screen-2xl mx-auto text-center">
          <h3 className={`font-bold text-slate-900 dark:text-white mb-6 ${isMobile ? 'text-3xl' : 'text-4xl'}`}>
            Still not sure?
          </h3>
          <p className={`text-black dark:text-slate-300 leading-relaxed max-w-2xl mx-auto ${isMobile ? 'text-lg' : 'text-xl'}`}>
            Reach out to us by clicking the WhatsApp icon, and we'll be happy to guide you to the ideal solution for your business.
          </p>
          <div className="mt-8 relative z-10">
            <a
              href="https://wa.me/9613309293"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-emerald-500 hover:bg-emerald-600 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-xl relative z-10"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="h-6 w-6 fill-current">
                <path d="M16 3C9.4 3 4 8.4 4 15c0 2.3.7 4.5 2 6.4L4 29l7.9-1.9C13.8 28.3 14.9 29 16 29c6.6 0 12-5.4 12-12S22.6 3 16 3zm0 2c5.5 0 10 4.5 10 10s-4.5 10-10 10c-1 0-2-.2-2.9-.5l-.4-.1-.4.1-4.4 1.1 1.2-4.2.1-.4-.2-.4C7.4 18.9 7 17 7 15c0-5.5 4.5-10 10-10zm-4.5 5c-.2 0-.4 0-.6.2-.2.3-.7.7-.7 1.7s.7 2 1 2.4.6.8 1.2 1.4 2.2 1.8 4.2 2.5c2.1.8 2.5.5 3 .5.5 0 1.5-.7 1.7-1.4.2-.7.2-1.4.1-1.5 0-.1-.2-.2-.4-.3s-1.5-.8-1.7-.9-.4-.1-.6.1c-.2.3-.7 1-.8 1.1-.2.1-.4.2-.7.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.8-1.6-2.1-.2-.3 0-.4.1-.5s.4-.5.5-.7c.2-.2.2-.4.3-.5.1-.1.1-.3 0-.5s-.5-1.3-.7-1.7c-.2-.4-.4-.4-.6-.4h-.5z" />
              </svg>
              Get in Touch
            </a>
          </div>
        </div>
      </motion.div>

      <FloatingWhatsApp />
    </div>
  );
};

// Conversation scripts for different agent types
interface ConversationMessage {
  sender: 'customer' | 'agent';
  text: string;
  delay: number;
  typing?: number;
}

interface AgentConfig {
  name: string;
  conversations: ConversationMessage[][];
  colors: {
    headerBg: string;
    headerBgDark: string;
    headerText: string;
    headerTextDark: string;
    onlineDot: string;
    customerBubble: string;
    backgroundGradient: string;
    backgroundGradientDark: string;
  };
}

const agentConfigs: AgentConfig[] = [
  {
    name: 'Customer Service',
    colors: {
      headerBg: 'bg-gradient-to-r from-indigo-700 to-blue-600',
      headerBgDark: 'dark:from-indigo-700 dark:to-blue-600',
      headerText: 'text-indigo-100',
      headerTextDark: 'dark:text-indigo-100',
      onlineDot: 'bg-indigo-300',
      customerBubble: 'bg-indigo-600',
      backgroundGradient: 'from-indigo-50 to-blue-50',
      backgroundGradientDark: 'dark:from-slate-800 dark:to-slate-900',
    },
    conversations: [[
      { sender: 'customer', text: 'I have an issue with my recent order', delay: 1000 },
      { sender: 'agent', text: 'I\'m sorry to hear that! Let me help you resolve this. Can you share your order number?', delay: 500, typing: 1000 },
      { sender: 'customer', text: 'It\'s #12345', delay: 500 },
      { sender: 'agent', text: 'Thank you! I can see your order. What specific issue are you experiencing?', delay: 500, typing: 1000 },
      { sender: 'customer', text: 'The item arrived damaged', delay: 500 },
      { sender: 'agent', text: 'I apologize for the inconvenience. I\'ve initiated a replacement order for you. You\'ll receive tracking info shortly!', delay: 500, typing: 1000 },
    ]]
  },
  {
    name: 'Order Processing Agent',
    colors: {
      headerBg: 'bg-gradient-to-r from-blue-600 to-indigo-500',
      headerBgDark: 'dark:from-blue-600 dark:to-indigo-500',
      headerText: 'text-blue-100',
      headerTextDark: 'dark:text-blue-100',
      onlineDot: 'bg-blue-300',
      customerBubble: 'bg-blue-600',
      backgroundGradient: 'from-blue-50 to-indigo-50',
      backgroundGradientDark: 'dark:from-slate-800 dark:to-slate-900',
    },
    conversations: [[
      { sender: 'customer', text: 'I want to place an order', delay: 1000 },
      { sender: 'agent', text: 'Great! What would you like to order today?', delay: 500, typing: 1000 },
      { sender: 'customer', text: '2 large pizzas and a salad', delay: 500 },
      { sender: 'agent', text: 'Perfect! That\'ll be $32.50. Would you like delivery or pickup?', delay: 500, typing: 1000 },
      { sender: 'customer', text: 'Delivery please', delay: 500 },
      { sender: 'agent', text: 'Order placed! Your food will arrive in 30-45 minutes. Track your order in real-time! 🍕', delay: 500, typing: 1000 },
    ]]
  },
  {
    name: 'Lead Qualification Agent',
    colors: {
      headerBg: 'bg-gradient-to-r from-indigo-500 to-purple-700',
      headerBgDark: 'dark:from-indigo-500 dark:to-purple-700',
      headerText: 'text-indigo-100',
      headerTextDark: 'dark:text-indigo-100',
      onlineDot: 'bg-indigo-300',
      customerBubble: 'bg-indigo-600',
      backgroundGradient: 'from-indigo-50 to-purple-50',
      backgroundGradientDark: 'dark:from-slate-800 dark:to-slate-900',
    },
    conversations: [[
      { sender: 'customer', text: 'I\'m interested in your consulting services', delay: 1000 },
      { sender: 'agent', text: 'That\'s great! I\'d love to help. What type of consulting are you looking for?', delay: 500, typing: 1000 },
      { sender: 'customer', text: 'Business strategy for my startup', delay: 500 },
      { sender: 'agent', text: 'Perfect! How many employees does your startup have?', delay: 500, typing: 1000 },
      { sender: 'customer', text: 'About 15 people', delay: 500 },
      { sender: 'agent', text: 'Excellent! I\'ve scheduled a discovery call for you tomorrow at 3 PM. You\'ll receive calendar details shortly!', delay: 500, typing: 1000 },
    ]]
  },
  {
    name: 'Scheduling Agent',
    colors: {
      headerBg: 'bg-gradient-to-r from-purple-700 to-indigo-700',
      headerBgDark: 'dark:from-purple-700 dark:to-indigo-700',
      headerText: 'text-purple-100',
      headerTextDark: 'dark:text-purple-100',
      onlineDot: 'bg-purple-300',
      customerBubble: 'bg-purple-600',
      backgroundGradient: 'from-purple-50 to-indigo-50',
      backgroundGradientDark: 'dark:from-slate-800 dark:to-slate-900',
    },
    conversations: [[
      { sender: 'customer', text: 'Hi, I\'d like to book an appointment', delay: 1000 },
      { sender: 'agent', text: 'Hello! I\'d be happy to help you book an appointment. What date works for you?', delay: 500, typing: 1000 },
      { sender: 'customer', text: 'How about tomorrow at 2pm?', delay: 500 },
      { sender: 'agent', text: 'Perfect! I\'ve booked you for tomorrow at 2:00 PM. You\'ll receive a confirmation shortly.', delay: 500, typing: 1000 },
      { sender: 'agent', text: '✅ Appointment confirmed! See you tomorrow!', delay: 500, typing: 800 },
    ]]
  },
];

interface Message {
  id: number;
  sender: 'customer' | 'agent';
  text: string;
  timestamp: number;
  read?: boolean;
}

const WhatsAppChatFlow = ({ isMobile }: { isMobile: boolean }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [currentAgentIndex, setCurrentAgentIndex] = useState(0);
  const [currentAgentName, setCurrentAgentName] = useState(agentConfigs[0].name);

  const currentAgent = agentConfigs[currentAgentIndex];
  const currentConversation = currentAgent.conversations[0];

  const addMessage = useCallback((msg: ConversationMessage) => {
    const newMessage: Message = {
      id: Date.now() + Math.random(),
      sender: msg.sender,
      text: msg.text,
      timestamp: Date.now(),
      read: msg.sender === 'customer', // Mark customer messages as read immediately
    };

    setMessages(prev => [...prev, newMessage]);

    // Move to next message
    setTimeout(() => {
      setCurrentIndex(prev => prev + 1);
    }, msg.delay);
  }, []);

  useEffect(() => {
    if (!isAnimating || currentIndex >= currentConversation.length) {
      // Switch to next agent after a delay
      const switchTimer = setTimeout(() => {
        const nextAgentIndex = (currentAgentIndex + 1) % agentConfigs.length;
        setCurrentAgentIndex(nextAgentIndex);
        setCurrentAgentName(agentConfigs[nextAgentIndex].name);
        setMessages([]);
        setCurrentIndex(0);
        setIsAnimating(true);
      }, 1000);
      return () => clearTimeout(switchTimer);
    }

    const currentMessage = currentConversation[currentIndex];

    // Show typing indicator for agent messages
    if (currentMessage.sender === 'agent' && currentMessage.typing) {
      setIsTyping(true);
      const typingTimer = setTimeout(() => {
        setIsTyping(false);
        addMessage(currentMessage);
      }, currentMessage.typing);
      return () => clearTimeout(typingTimer);
    } else {
      addMessage(currentMessage);
    }
  }, [currentIndex, isAnimating, currentAgentIndex, currentConversation]);

  const currentColors = currentAgent.colors;

  return (
    <div className={`relative w-full ${isMobile ? 'h-[480px]' : 'h-[520px]'}`}>
      {/* WhatsApp Chat Interface */}
      <motion.div
        key={currentAgentIndex}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.4, ease: "easeInOut" }}
        className={`relative bg-gradient-to-br ${currentColors.backgroundGradient} ${currentColors.backgroundGradientDark} rounded-2xl shadow-2xl overflow-hidden border border-slate-200/50 dark:border-slate-700/50 h-full flex flex-col`}
      >
        {/* Header */}
        <motion.div
          key={`header-${currentAgentIndex}`}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className={`${currentColors.headerBg} ${currentColors.headerBgDark} px-4 py-2.5 flex items-center gap-3`}
        >
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="h-5 w-5 fill-white">
              <path d="M16 3C9.4 3 4 8.4 4 15c0 2.3.7 4.5 2 6.4L4 29l7.9-1.9C13.8 28.3 14.9 29 16 29c6.6 0 12-5.4 12-12S22.6 3 16 3zm0 2c5.5 0 10 4.5 10 10s-4.5 10-10 10c-1 0-2-.2-2.9-.5l-.4-.1-.4.1-4.4 1.1 1.2-4.2.1-.4-.2-.4C7.4 18.9 7 17 7 15c0-5.5 4.5-10 10-10zm-4.5 5c-.2 0-.4 0-.6.2-.2.3-.7.7-.7 1.7s.7 2 1 2.4.6.8 1.2 1.4 2.2 1.8 4.2 2.5c2.1.8 2.5.5 3 .5.5 0 1.5-.7 1.7-1.4.2-.7.2-1.4.1-1.5 0-.1-.2-.2-.4-.3s-1.5-.8-1.7-.9-.4-.1-.6.1c-.2.3-.7 1-.8 1.1-.2.1-.4.2-.7.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.8-1.6-2.1-.2-.3 0-.4.1-.5s.4-.5.5-.7c.2-.2.2-.4.3-.5.1-.1.1-.3 0-.5s-.5-1.3-.7-1.7c-.2-.4-.4-.4-.6-.4h-.5z" />
            </svg>
          </div>
          <div className="flex-1">
            <motion.div
              key={currentAgentName}
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`text-white font-semibold text-sm`}
            >
              {currentAgentName}
            </motion.div>
            <div className={`${currentColors.headerText} ${currentColors.headerTextDark} text-xs flex items-center gap-1`}>
              <div className={`w-2 h-2 ${currentColors.onlineDot} rounded-full animate-pulse`} />
              Online
            </div>
          </div>
        </motion.div>

        {/* Messages Area */}
        <div className="flex-1 overflow-hidden p-3 space-y-2 flex flex-col">
          <div className="flex-1 overflow-hidden space-y-2 pr-2">
            {messages.map((msg, idx) => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
                className={`flex ${msg.sender === 'customer' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[75%] ${msg.sender === 'customer' ? 'order-2' : 'order-1'}`}>
                  <motion.div
                    className={`rounded-xl px-3 py-1.5 ${msg.sender === 'customer'
                      ? `${currentColors.customerBubble} text-white rounded-tr-sm`
                      : 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 rounded-tl-sm'
                      } shadow-md relative`}
                  >
                    <p className="text-xs leading-relaxed">{msg.text}</p>
                    {msg.sender === 'customer' && (
                      <div className="flex items-center justify-end gap-1 mt-0.5">
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: msg.read ? 1 : 0 }}
                          transition={{ delay: 0.5 }}
                        >
                          {msg.read ? (
                            <CheckCheck className="h-2.5 w-2.5 text-blue-300" />
                          ) : (
                            <Check className="h-2.5 w-2.5" />
                          )}
                        </motion.div>
                      </div>
                    )}
                  </motion.div>
                </div>
              </motion.div>
            ))}

            {/* Typing Indicator */}
            {isTyping && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex justify-start"
              >
                <div className="bg-white dark:bg-slate-700 rounded-xl rounded-tl-sm px-3 py-2 shadow-md">
                  <div className="flex gap-1.5 items-center">
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="w-2 h-2 bg-slate-500 rounded-full"
                        animate={{
                          opacity: [0.3, 1, 0.3]
                        }}
                        transition={{
                          duration: 1.2,
                          repeat: Infinity,
                          delay: i * 0.2,
                          ease: "easeInOut"
                        }}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const FloatingWhatsApp = () => (
  <a
    href="https://wa.me/9613309293"
    target="_blank"
    rel="noopener noreferrer"
    className="fixed bottom-6 right-6 z-50 flex items-center bg-emerald-500 hover:bg-emerald-600 text-white h-12 px-3 rounded-full shadow-2xl transition-all duration-500 ease-out overflow-hidden group w-12 hover:w-64"
  >
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" className="h-6 w-6 fill-current flex-shrink-0">
      <path d="M16 3C9.4 3 4 8.4 4 15c0 2.3.7 4.5 2 6.4L4 29l7.9-1.9C13.8 28.3 14.9 29 16 29c6.6 0 12-5.4 12-12S22.6 3 16 3zm0 2c5.5 0 10 4.5 10 10s-4.5 10-10 10c-1 0-2-.2-2.9-.5l-.4-.1-.4.1-4.4 1.1 1.2-4.2.1-.4-.2-.4C7.4 18.9 7 17 7 15c0-5.5 4.5-10 10-10zm-4.5 5c-.2 0-.4 0-.6.2-.2.3-.7.7-.7 1.7s.7 2 1 2.4.6.8 1.2 1.4 2.2 1.8 4.2 2.5c2.1.8 2.5.5 3 .5.5 0 1.5-.7 1.7-1.4.2-.7.2-1.4.1-1.5 0-.1-.2-.2-.4-.3s-1.5-.8-1.7-.9-.4-.1-.6.1c-.2.3-.7 1-.8 1.1-.2.1-.4.2-.7.1-.3-.1-1.2-.4-2.3-1.4-.9-.8-1.4-1.8-1.6-2.1-.2-.3 0-.4.1-.5s.4-.5.5-.7c.2-.2.2-.4.3-.5.1-.1.1-.3 0-.5s-.5-1.3-.7-1.7c-.2-.4-.4-.4-.6-.4h-.5z" />
    </svg>
    <span className="ml-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap font-medium">
      Contact us on WhatsApp
    </span>
  </a>
);

export default Integrations;
